import React, {useMemo, useCallback, useState, useEffect} from 'react';
import {Box, SvgIcon, Typography, ButtonBase} from '@mui/material';
import {useTranslation} from 'react-i18next';
import ManageIndicesHeader from './header/ManageIndicesHeader';
import './ManageIndices.scss';
import {ReactComponent as DownloadIcon} from '../../../../assets/images/download_icon.svg';
import {ReactComponent as LeftArrow} from '../../../../assets/images/left-arrow.svg';
import {useLocation, useNavigate} from 'react-router-dom';
import ManageIndicesDataGrid from './grid/ManageIndicesDataGrid';
import type {ManageIndicesGridQuery} from './grid/ManageIndicesDataGrid';
import ArrToastNotification from '../../../common/arr-toast-notification/ArrToastNotification';
import {
  BgNavigationTypes,
  MANAGE_INDEX_DATA_UPDATED_NOTIFICATION,
  SourceTypeCode,
  ToastType,
} from '../../../../shared/constants';
import CreateNewIndex from './add-edit-index/create-index/CreateNewIndex';
import {
  IAIInvestigateIndicesListItem,
  IAIInvestigateIndicesPayload,
  ManageIndexDataUpdatedNotification,
  ManageIndexRow,
} from '../../../../api/types/aiInvestigate.type';
import {
  useGetAIInvestigateIndicesMasterDocListId,
  useGetIndicesList,
  useToggleIndex,
} from '../../../../hooks/ai/investigate/useInvestigate';
import {getCsvDownloadForManageIndices} from '../../../../api/endpoints/aiInvestigateApi';
import {useDispatch, useSelector} from 'react-redux';
import {
  setDocListBackNavigation,
  setDocListId,
} from '../../../../redux/slice/docListSlice';
import {RootState} from '../../../../redux/store';
import ArrLoadingSpinner from '../../../common/arr-loading-spinner/ArrLoadingSpinner';

type HeaderButtonDetail = {
  title?: string;
  onClick: () => void;
  icon?: React.ReactNode;
  iconOnly?: boolean;
  ariaLabel?: string;
  disabled?: boolean;
  tooltip?: string;
  dataTestId: string;
};

const srOnlyStyles = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: 1,
  margin: -1,
  overflow: 'hidden',
  padding: 0,
  position: 'absolute' as const,
  width: 1,
  whiteSpace: 'nowrap' as const,
};

const ManageIndices: React.FC = () => {
  const {t} = useTranslation();
  const [openCreate, setOpenCreate] = useState<boolean>(false);
  const [showToast, setShowToast] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string>('');
  const [toastStatus, setToastStatus] = useState<ToastType>(ToastType.Success);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const row = location.state;
  const lastEvent = useSelector((state: RootState) => state.signalr.lastEvent);
  const docListMasterId = useSelector(
    (state: RootState) => state.docList.docListId
  );

  const [isNavigating, setIsNavigating] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [rows, setRows] = useState<ManageIndexRow[]>([]);
  const [rowCount, setRowCount] = useState<number>(0);
  const [gridQuery, setGridQuery] = useState<ManageIndicesGridQuery>({
    pageNumber: 1,
    pageSize: 25,
    sortField: 'lastModifiedUtc',
    sortDirection: 'DESC',
    filters: [],
  });

  const formatLastModifiedUtc = useCallback((value?: string | null) => {
    if (!value) return '';
    const raw = value.trim();
    if (!raw) return '';
    const normalized = raw.replace('T', ' ').replace('Z', '');
    const [datePart = '', timeWithMs = ''] = normalized.split(' ');
    const [yyyy = '', mm = '', dd = ''] = datePart.split('-');
    const timePart = timeWithMs.split('.')[0];
    const [hh = '00', min = '00', sec = '00'] = timePart.split(':');
    if (!yyyy || !mm || !dd) return raw;

    return `${yyyy}/${mm}/${dd} ${hh}:${min}:${sec} UTC`;
  }, []);

  const mapApiItemToRow = useCallback(
    (item: IAIInvestigateIndicesListItem, idx: number): ManageIndexRow => {
      const parsedId = Number(item.indexId);
      return {
        id: Number.isFinite(parsedId) ? parsedId : idx + 1,
        indexName: item.indexName ?? '',
        updatedBy: item.updatedBy ?? '',
        indexId: item.indexId ?? '',
        active: String(item.status).toLowerCase() === 'true',
        totalDocs: Number(item.totalDocs ?? 0),
        lastModifiedUtc: formatLastModifiedUtc(item.lastModifiedDate),
        indexStatus: item.indexStatus ?? '',
      };
    },
    [formatLastModifiedUtc]
  );

  const mapSortFieldToApiColumn = useCallback((sortField?: string) => {
    switch (sortField) {
      case 'indexName':
        return 'IndexName';
      case 'updatedBy':
        return 'UpdatedBy';
      case 'indexId':
        return 'IndexId';
      case 'active':
        return 'Status';
      case 'totalDocs':
        return 'TotalDocs';
      case 'indexStatus':
        return 'IndexStatus';
      case 'lastModifiedUtc':
      default:
        return 'LastModifiedDate';
    }
  }, []);

  const indicesPayload: IAIInvestigateIndicesPayload = useMemo(
    () => ({
      sortingColumn: mapSortFieldToApiColumn(gridQuery.sortField),
      direction: gridQuery.sortDirection ?? 'DESC',
      displayLength: gridQuery.pageSize,
      pageNumber: gridQuery.pageNumber,
      columnFilter: gridQuery.filters,
    }),
    [gridQuery, mapSortFieldToApiColumn]
  );

  const {
    data: indicesData,
    refetch: refetchIndices,
    isFetching,
  } = useGetIndicesList(indicesPayload);

  useEffect(() => {
    const mappedRows = (indicesData?.indices ?? []).map(mapApiItemToRow);
    setRows(mappedRows);
    setRowCount(Number(indicesData?.totalRecords ?? 0));
  }, [indicesData, mapApiItemToRow]);

  const hasData = rows.length > 0;

  const handleCreateNewIndex = useCallback(() => {
    setOpenCreate(true);
  }, []);

  // Listen to signalR messages and update the grid data if the message is related to index details data update
  useEffect(() => {
    if (lastEvent?.payload?.type !== MANAGE_INDEX_DATA_UPDATED_NOTIFICATION)
      return;
    const notification: ManageIndexDataUpdatedNotification =
      lastEvent!.payload.payload;
    if (!rows.some((row) => row.id === notification.indexId)) return;

    setRows((prevRows) =>
      prevRows.map((row) =>
        row.id === notification.indexId
          ? {
              ...row,
              totalDocs: notification.totalDocs,
              indexStatus: notification.indexStatus,
            }
          : row
      )
    );
  }, [lastEvent]);

  const buildQueryParams = () => {
    if (!gridQuery.filters?.length) return undefined;

    const COLUMN_MAP: Record<string, string> = {
      indexname: 'IndexName',
      indexid: 'IndexID',
      updatedby: 'UpdatedBy',
    };

    const params = gridQuery.filters.reduce(
      (acc, {columnName, value}: any) => {
        if (!columnName || value == null || value === '') return acc;

        const key = COLUMN_MAP[columnName.toLowerCase()];
        if (key) acc[key] = value;

        return acc;
      },
      {} as Record<string, any>
    );

    return Object.keys(params).length ? params : undefined;
  };
  const handleDownload = useCallback(async () => {
    if (!hasData) return;

    try {
      const queryParams = buildQueryParams();

      const response = await getCsvDownloadForManageIndices(queryParams);

      const {blob, filename} = response;

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'document.csv';

      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed', error);
      setToastMessage(t('ai.investigate.manage-indices.downloadError'));
      setToastStatus(ToastType.Error);
      setShowToast(true);
    }
  }, [hasData, gridQuery.filters]); // keep this

  const handleIndexNameClick = useCallback((row: ManageIndexRow) => {
    navigate('/AI/Investigate/manage-indices/details', {state: row});
  }, []);

  const {mutate: getIndicesMasterDocListId} =
    useGetAIInvestigateIndicesMasterDocListId((response) => {
      dispatch(setDocListId(Number(response.doclistMasterId)));
    });

  const handleDocsClick = useCallback(
    (row: ManageIndexRow) => {
      getIndicesMasterDocListId(
        {
          indexId: row.indexId,
        },
        {
          onSuccess: (res) => {
            setIsNavigating(true);
            setIsLoading(true);
          },
        }
      );
    },
    [getIndicesMasterDocListId]
  );

  // Check the signalR message navigate accordingly
  useEffect(() => {
    if (!isNavigating) return;

    const {navigationDestinationType: navType, notificationId} =
      lastEvent?.payload?.payload || {};

    if (lastEvent?.payload?.type === BgNavigationTypes.NAV_PAYLOAD_COMPLETE) {
      if (
        notificationId === Number(docListMasterId) &&
        navType === SourceTypeCode.DOCLIST
      ) {
        setIsLoading(false);
        setIsNavigating(false);
        const backPath = `${window.location.pathname}${window.location.search}`;
        dispatch(
          setDocListBackNavigation({
            label: t('ai.investigate.manageIndices'),
            path: backPath,
            navState: row,
          })
        );
        const newWindow = window.open('about:blank', '_blank');
        if (newWindow) {
          newWindow.location.href = `/Document/DocList`;
        }
      }
      return;
    }

    if (
      lastEvent?.payload?.type === BgNavigationTypes.NAV_PAYLOAD_ERROR &&
      Number(notificationId) === 0
    ) {
      setIsLoading(false);
      setIsNavigating(false);
      return;
    }
  }, [lastEvent, docListMasterId]);

  const toastHandler = useCallback((message: string, status: ToastType) => {
    setToastMessage(message);
    setToastStatus(status);
    setShowToast(true);
  }, []);

  const {mutate: toggleIndex} = useToggleIndex();

  const handleActiveToggleChange = useCallback(
    (row: ManageIndexRow, checked: boolean) => {
      const previousValue = row.active;

      setRows((prev) =>
        prev.map((item) =>
          item.id === row.id ? {...item, active: checked} : item
        )
      );

      toggleIndex(
        {
          indexId: row.indexId,
          payload: {status: checked},
        },
        {
          onSuccess: () => {
            toastHandler(
              t('ai.investigate.manageIndicesDetail.toggleIndex.success', {
                action: t(
                  checked
                    ? 'ai.investigate.manageIndicesDetail.toggleIndex.activated'
                    : 'ai.investigate.manageIndicesDetail.toggleIndex.deactivated'
                ),
              }),
              ToastType.Success
            );
          },
          onError: () => {
            setRows((prev) =>
              prev.map((item) =>
                item.id === row.id ? {...item, active: previousValue} : item
              )
            );

            toastHandler(
              t('ai.investigate.manageIndicesDetail.toggleIndex.error', {
                action: t(
                  checked
                    ? 'ai.investigate.manageIndicesDetail.toggleIndex.activating'
                    : 'ai.investigate.manageIndicesDetail.toggleIndex.deactivating'
                ),
              }),
              ToastType.Error
            );
          },
        }
      );
    },
    [toggleIndex, toastHandler, t]
  );
  const handleGridQueryChange = useCallback(
    (nextQuery: ManageIndicesGridQuery) => {
      setGridQuery((prev) => {
        const normalizedNextQuery: ManageIndicesGridQuery = {
          ...nextQuery,
          sortField: nextQuery.sortField ?? prev.sortField,
          sortDirection: nextQuery.sortDirection ?? prev.sortDirection,
        };
        const prevSerialized = JSON.stringify(prev);
        const nextSerialized = JSON.stringify(normalizedNextQuery);
        return prevSerialized === nextSerialized ? prev : normalizedNextQuery;
      });
    },
    []
  );

  const buttonDetails: HeaderButtonDetail[] = useMemo(
    () => [
      {
        onClick: handleDownload,
        iconOnly: true,
        disabled: !hasData,
        ariaLabel: 'Download indices',
        tooltip: 'Download',
        dataTestId: 'Download',
        icon: (
          <SvgIcon
            component={DownloadIcon}
            inheritViewBox
            aria-hidden="true"
            focusable="false"
          />
        ),
      },
      {
        title: `${t('ai.investigate.chatPanel.createNewIndex')}  +`,
        onClick: handleCreateNewIndex,
        dataTestId: 'create-new',
      },
    ],
    [t, handleCreateNewIndex, handleDownload, hasData]
  );

  const closePanel = useCallback(() => setOpenCreate(false), []);

  const handleCreated = useCallback(() => {
    refetchIndices();
  }, [refetchIndices]);

  return (
    <Box
      className="manage-indices-container"
      component="div"
      role="region"
      aria-labelledby="manage-indices-title"
      aria-describedby="manage-indices-description"
    >
      <Typography
        id="manage-indices-description"
        component="p"
        style={srOnlyStyles}
        data-testid="manage-indices-header-tooltip"
      >
        {t('ai.investigate.manage-indices.headerTooltip')}
      </Typography>
      <Box className="manage-indices-header">
        <ManageIndicesHeader
          title={t('ai.investigate.manageIndices')}
          titleId="manage-indices-title"
          tooltipContent={t(
            'ai.investigate.manage-indices.headerTooltipLongDescription'
          )}
          buttonDetails={buttonDetails}
        />
      </Box>
      <Box
        className="breadcrumb"
        component="nav"
        aria-label={t('breadcrumb', 'Breadcrumb')}
      >
        <ButtonBase
          onClick={() => navigate('/AI/Investigate')}
          aria-label={t('ai.investigate.manage-indices.breadcrumbLinkAria')}
          className="breadcrumb-content"
          data-testid="navigate-ai-investigate"
        >
          <Box className="icon-box" aria-hidden="true">
            <LeftArrow
              aria-hidden="true"
              focusable="false"
              className="left-arrow"
            />
          </Box>
          <Typography
            component="span"
            className="breadcrumb-text"
            data-testid="manage-indices-breadcrumb"
          >
            {t('ai.investigate.manage-indices.breadcrumb')}
          </Typography>
        </ButtonBase>
      </Box>
      <Box className="manage-indices-body">
        {isLoading && <ArrLoadingSpinner overlay isOpen={isLoading} />}
        <ManageIndicesDataGrid
          rows={rows}
          rowCount={rowCount}
          loading={isFetching}
          setRows={setRows}
          onIndexNameClick={handleIndexNameClick}
          onTotalDocsClick={handleDocsClick}
          onActiveToggleChange={handleActiveToggleChange}
          onQueryChange={handleGridQueryChange}
        />
      </Box>
      {openCreate && (
        <CreateNewIndex
          openPanel={openCreate}
          onClose={closePanel}
          onCreated={handleCreated}
          showToast={toastHandler}
        />
      )}
      {showToast && (
        <ArrToastNotification
          message={toastMessage}
          onClose={() => {
            setToastMessage('');
            setShowToast(false);
          }}
          status={[toastStatus]}
        />
      )}
    </Box>
  );
};

export default ManageIndices;
